import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import config from "../../config/config";
import CockpitShell from "../../components/CockpitShell";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { authHeaders, formatDateTime, wtNavItems } from "./wtShared";

const WtResult = () => {
  const navigate = useNavigate();
  const { attemptId } = useParams();
  const [attemptData, setAttemptData] = useState(null);
  const [applications, setApplications] = useState([]);
  const [shareBewerbungId, setShareBewerbungId] = useState("");
  const [shareMessage, setShareMessage] = useState("");
  const [error, setError] = useState("");

  const attempt = attemptData?.attempt || null;
  const questions = attemptData?.questions || [];

  useEffect(() => {
    document.title = "Experts4Europe | Wissenstest Ergebnis";
    (async () => {
      try {
        const [detailRes, appsRes] = await Promise.all([
          axios.get(`${config.API_BASE_URL}/api/wt/attempts/${attemptId}/result`, { headers: authHeaders() }),
          axios.get(`${config.API_BASE_URL}/api/stb/me/applications`, { headers: authHeaders() }),
        ]);
        if (detailRes.data?.attempt?.status !== "submitted") {
          navigate(`/assessment/wt/run/${attemptId}`, { replace: true });
          return;
        }
        setAttemptData(detailRes.data);
        setApplications(appsRes.data?.rows || []);
        setError("");
      } catch (e) {
        setError(e.response?.data?.message || "Ergebnis konnte nicht geladen werden.");
      }
    })();
  }, [attemptId]);

  const shareResult = async () => {
    if (!shareBewerbungId) return;
    try {
      const res = await axios.post(
        `${config.API_BASE_URL}/api/wt/attempts/${attemptId}/share`,
        { bewerbung_id: Number(shareBewerbungId) },
        { headers: authHeaders() }
      );
      setShareMessage(`Ergebnis geteilt als ${res.data?.candidate_ref || "CAND-REF"}.`);
    } catch (e) {
      setShareMessage(e.response?.data?.message || "Teilen fehlgeschlagen.");
    }
  };

  const revokeShare = async () => {
    if (!shareBewerbungId) return;
    try {
      await axios.delete(`${config.API_BASE_URL}/api/wt/attempts/${attemptId}/share`, {
        params: { bewerbung_id: Number(shareBewerbungId) },
        headers: authHeaders(),
      });
      setShareMessage("Freigabe wurde widerrufen.");
    } catch (e) {
      setShareMessage(e.response?.data?.message || "Widerruf fehlgeschlagen.");
    }
  };

  return (
    <CockpitShell
      title="Wissenstest"
      subtitle="Dein Ergebnis und optional anonymes Teilen mit einer Bewerbung."
      badge="Ergebnis"
      navItems={wtNavItems}
      actions={
        <Button className="bg-cyan-500 text-slate-950 hover:bg-cyan-300" onClick={() => navigate("/assessment/wt")}>
          Neuen Test starten
        </Button>
      }
    >
      {error ? <p className="rounded-lg border border-rose-300/30 bg-rose-500/10 p-3 text-sm text-rose-100">{error}</p> : null}

      {attempt ? (
        <Card className="card-e4e">
          <CardHeader><CardTitle className="font-display text-white">Dein Ergebnis</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-xl border border-emerald-300/30 bg-emerald-500/10 p-4">
              <p className="text-sm text-emerald-100">Testlauf vom {formatDateTime(attempt.finished_at || attempt.started_at)}</p>
              <p className="mt-1 text-3xl font-bold text-white">{attempt.score_percent}%</p>
              <p className="text-sm text-slate-200">{attempt.correct_count}/{attempt.total_count} richtig</p>
            </div>

            <Accordion type="single" collapsible className="rounded-xl border border-white/10 px-3">
              <AccordionItem value="answers" className="border-b-0">
                <AccordionTrigger className="text-slate-100 hover:no-underline">Antworten ansehen</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    {questions.map((q) => (
                      <div key={q.question_id} className="rounded-lg border border-white/10 bg-slate-900/40 p-3 text-sm">
                        <p className="font-semibold text-white">{q.question}</p>
                        <p className="mt-1 text-xs text-slate-300">
                          Status: {q.was_expired ? "Zeit abgelaufen" : (q.is_correct ? "Richtig" : "Nicht richtig")}
                        </p>
                        <p className="mt-1 text-xs text-slate-300">Erklaerung: {q.explanation || "-"}</p>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <div className="rounded-xl border border-cyan-300/20 bg-cyan-500/10 p-4">
              <p className="font-semibold text-cyan-100">Ergebnis anonym mit Bewerbung teilen</p>
              <p className="mt-1 text-xs text-slate-200">Arbeitgeber sieht nur eine anonymisierte Zusammenfassung.</p>
              <div className="mt-2 grid gap-2 md:grid-cols-[1fr_auto_auto]">
                <Select value={shareBewerbungId} onValueChange={setShareBewerbungId}>
                  <SelectTrigger className="border-white/20 bg-slate-950/40 text-white"><SelectValue placeholder="Bewerbung auswaehlen" /></SelectTrigger>
                  <SelectContent>
                    {applications.map((a) => (
                      <SelectItem key={a.id} value={String(a.id)}>
                        {a.titel || a.beruf_text || "Anzeige"} · {Number(a.share_wt) === 1 ? "Sharing aktiv" : "Sharing deaktiviert"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button className="bg-cyan-500 text-slate-950 hover:bg-cyan-300" disabled={!shareBewerbungId} onClick={shareResult}>Teilen</Button>
                <Button variant="outline" className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10" disabled={!shareBewerbungId} onClick={revokeShare}>Widerrufen</Button>
              </div>
              {shareMessage ? <p className="mt-2 text-xs text-cyan-100">{shareMessage}</p> : null}
            </div>

            <div className="flex justify-end">
              <Button variant="outline" className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10" onClick={() => navigate("/assessment/wt/history")}>
                Meine Ergebnisse
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </CockpitShell>
  );
};

export default WtResult;
